import React, { createContext, useContext, useState, useCallback } from 'react';
import { AlertTriangle, Trash2, Info, CheckCircle, X } from 'lucide-react';

/**
 * Modal di conferma globale, sostituisce window.confirm (che è bloccato/inaffidabile
 * in alcuni browser/contesti, e ha UX terribile).
 *
 * Uso:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: '...', message: '...', danger: true });
 *   if (!ok) return;
 */
const ConfirmContext = createContext(() => Promise.resolve(false));

export const useConfirm = () => useContext(ConfirmContext);

const VARIANTS = {
  danger:  { icon: Trash2,         iconBg: 'bg-red-100',    iconColor: 'text-red-600',    btn: 'bg-red-600 hover:bg-red-700' },
  warning: { icon: AlertTriangle,  iconBg: 'bg-amber-100',  iconColor: 'text-amber-600',  btn: 'bg-amber-600 hover:bg-amber-700' },
  success: { icon: CheckCircle,    iconBg: 'bg-green-100',  iconColor: 'text-green-600',  btn: 'bg-green-600 hover:bg-green-700' },
  info:    { icon: Info,           iconBg: 'bg-blue-100',   iconColor: 'text-blue-600',   btn: 'bg-blue-600 hover:bg-blue-700' },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, confirmLabel, cancelLabel, variant, resolve }

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        title: opts?.title || 'Conferma',
        message: opts?.message || 'Sei sicuro?',
        confirmLabel: opts?.confirmLabel || 'Conferma',
        cancelLabel: opts?.cancelLabel || 'Annulla',
        variant: opts?.variant || (opts?.danger ? 'danger' : 'warning'),
        resolve,
      });
    });
  }, []);

  const close = (result) => {
    if (state?.resolve) state.resolve(result);
    setState(null);
  };

  const v = VARIANTS[state?.variant] || VARIANTS.warning;
  const Icon = v.icon;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => close(false)} data-testid="confirm-dialog">
          <div className="bg-white rounded-t-2xl sm:rounded-lg w-full sm:max-w-md shadow-2xl animate-fade-in overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start gap-4">
                <span className={`flex-shrink-0 w-12 h-12 rounded-full ${v.iconBg} flex items-center justify-center`}>
                  <Icon size={22} className={v.iconColor} />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-black text-stone-900 mb-1.5">{state.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-wrap">{state.message}</p>
                </div>
                <button onClick={() => close(false)} className="text-stone-400 hover:text-stone-600 p-1" aria-label="Chiudi">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-stone-50 border-t border-stone-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button onClick={() => close(false)} data-testid="confirm-cancel"
                className="px-5 py-2.5 text-sm font-bold text-stone-700 bg-white border border-stone-300 rounded-md hover:bg-stone-100 transition-colors">
                {state.cancelLabel}
              </button>
              <button onClick={() => close(true)} data-testid="confirm-ok" autoFocus
                className={`px-5 py-2.5 text-sm font-bold text-white rounded-md transition-colors shadow-md ${v.btn}`}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
